import { z } from 'zod';
import { PolicyRuleStatusSchema } from './approval_verdict';
import { VoucherViewSchema } from './traveler_view';
import { LocaleSchema } from './travel_preferences';
import { QuotationFlightFactsSchema } from './voucher_request';

/**
 * Canonical wire contract for the AGENTIC APPROVAL triage read model: the rich
 * per-voucher facts a downstream triage engine (Phase 2, `voucher-events`) needs
 * to bucket/explain a pending voucher, served by the gated customer-api
 * `.../pending-approvals/triage-source` route (approver-scoped, VPC-only,
 * un-scrubbed — it carries `externalCode` PII, which NEVER crosses `scrubVoucher`
 * onto a `VoucherView`).
 *
 * This is the READ (served) shape — the mirror STORAGE shape lives on the
 * customer-api voucher document. The rich block stays MIRROR-ONLY: it is off the
 * generic `Voucher`/`VoucherView` (which gains only the aggregate `within`) and
 * is stripped in `docToVoucher`, exactly like the mirror-only approver list.
 *
 * Field names are OURS (domain vocabulary) — the customer-api mapper/builder
 * translates the foreign source names. Raw foreign source field names never
 * appear here (see design doc §10 for the source-to-view mapping).
 *
 * Dates that must preserve wall-clock fidelity (`expiration`, action timestamps,
 * candidate flight/hotel dates) are carried as RAW ISO strings — never
 * `z.coerce.date()` (voucher-date-fidelity rule).
 *
 * The carry list is the design doc §10 (carry-in-mirror spec): every CARREGAR row
 * maps to a field here; CORTAR rows are excluded; `fareFlexibility` is the one
 * DERIVE@ingest (3 bools distilled from `fares.tariffDetails`).
 */

/** One localized rule text (name + prose), as persisted trilingually on the source. */
const RuleTextSchema = z.object({ name: z.string(), description: z.string() });

/** One evaluated policy rule: machine status + the display prose in three locales. */
const PolicyRuleEvalSchema = z.object({
  status: PolicyRuleStatusSchema,
  descriptions: z.object({ pt: RuleTextSchema, es: RuleTextSchema, en: RuleTextSchema }),
});

/**
 * Per-client policy-compliance flags. DYNAMIC-KEY by design (ACL-02): the key set
 * varies per client policy (`cheapestPricePeriod` vs `cheapestPrice` vs
 * `cheapestHotel` vs `bookingWindow`…) — never a fixed object.
 */
const PolicyFlagsSchema = z.record(z.string(), z.boolean());

const PolicyEvaluationSchema = z.object({
  within: z.boolean(),
  name: z.string().nullable(),
  rules: z.array(PolicyRuleEvalSchema),
});

const PricingBenchmarkSchema = z.object({
  amount: z.number().nullable(),
  currency: z.string().nullable(),
  dailyRate: z.number().nullable().optional(),
  marketPrice: z.number().nullable().optional(),
  savingTotal: z.number().nullable().optional(),
  fees: z
    .object({
      fee: z.number().nullable(),
      boardingFee: z.number().nullable(),
      serviceFee: z.number().nullable(),
    })
    .nullable()
    .optional(),
});

/** Who took an action, and when (raw ISO — fidelity rule). */
const ActionActorSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
  at: z.string().nullable(),
});

const ActionsSchema = z.object({
  requested: ActionActorSchema.nullable(),
  support: z.array(ActionActorSchema.extend({ justification: z.string().nullable() })).optional(),
  approved: z.array(ActionActorSchema).optional(),
});

const CostCenterAllocationSchema = z.object({
  company: z.object({ name: z.string().nullable() }).nullable(),
  costCenter: z
    .object({
      name: z.string().nullable(),
      // PII (external accounting code) — gated route only.
      externalCode: z.string().nullable(),
    })
    .nullable(),
  project: z.object({ name: z.string().nullable() }).nullable().optional(),
  percent: z.number().nullable(),
});

const ApprovalContextSchema = z.object({
  motive: z
    .object({ id: z.union([z.number(), z.string()]).nullable(), name: z.string().nullable() })
    .nullable(),
  obs: z.string().nullable(),
  justification: z.object({ id: z.string().nullable(), name: z.string().nullable() }).nullable(),
  costCenters: z.array(CostCenterAllocationSchema),
});

/** A resolved approver in the approval flow; `delegateBy` present when substituted. */
const ApproverSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string().nullable(),
  email: z.string().nullable(),
  // PII — gated route only, never scrubbed onto a VoucherView.
  externalCode: z.string().nullable().optional(),
  delegateBy: z
    .object({
      id: z.union([z.number(), z.string()]),
      name: z.string().nullable(),
      email: z.string().nullable(),
    })
    .nullable()
    .optional(),
  undeterminedTime: z.boolean().optional(),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
});

/** Traveler / requester inline identity; `changed` flags a swapped requester. */
const TriagePartySchema = z.object({
  id: z.union([z.number(), z.string()]).nullable(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  externalCode: z.string().nullable().optional(),
  changed: z.boolean().optional(),
});

const OriginSchema = z.object({
  credential: z.string().nullable().optional(),
  channel: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  banking: z.string().nullable().optional(),
  reasonOffline: z
    .array(z.object({ message: z.array(z.string()), location: z.string().nullable() }))
    .nullable()
    .optional(),
});

const FlowFlagsSchema = z.object({
  forceApproveBeforeQuoting: z.boolean().nullable().optional(),
  hasControlLimit: z.boolean().nullable().optional(),
});

// ── Flight adapter ────────────────────────────────────────────────────────────

const FlightCandidateSchema = z.object({
  supplier: z.string().nullable(),
  total: z.object({ value: z.number().nullable(), currency: z.string().nullable() }).nullable(),
  flight: QuotationFlightFactsSchema.nullable(),
  policies: PolicyFlagsSchema,
  within: z.boolean().nullable(),
});

const FlightLegSchema = z.object({
  cheapest: FlightCandidateSchema.nullable(),
  expensive: FlightCandidateSchema.nullable(),
});

const FlightAdapterSchema = z.object({
  kind: z.literal('FLIGHT'),
  quotationFlight: z
    .object({
      chosen: z
        .object({
          bundled: z.boolean().nullable(),
          departing: FlightCandidateSchema.nullable(),
          returning: FlightCandidateSchema.nullable(),
        })
        .nullable(),
      summary: z
        .object({
          departing: FlightLegSchema.nullable(),
          returning: FlightLegSchema.nullable(),
        })
        .nullable(),
    })
    .nullable(),
  // DERIVE@ingest — distilled from fares.tariffDetails, not the raw array.
  // true = permitted (included in the fare OR for a fee), false = restricted or
  // unavailable on at least one leg, null = the fare does not say.
  fareFlexibility: z
    .object({
      refundable: z.boolean().nullable(),
      changeable: z.boolean().nullable(),
      cancellable: z.boolean().nullable(),
    })
    .nullable(),
});

// ── Hotel adapter ───────────────────────────────────────────────────────────

/** Hotel candidate = flat name + optional total (some rows have NO total). */
const HotelCandidateSchema = z.object({
  supplier: z.string().nullable(),
  total: z
    .object({ value: z.number().nullable(), currency: z.string().nullable() })
    .nullable()
    .optional(),
});

const HotelAdapterSchema = z.object({
  kind: z.literal('HOTEL'),
  hotelStay: z
    .object({
      checkinDate: z.string().nullable(),
      checkoutDate: z.string().nullable(),
      numNights: z.number().nullable(),
      valuePerNight: z.number().nullable(),
      dailyRate: z.number().nullable(),
    })
    .nullable(),
  hotelFlexibility: z
    .object({
      cancelationPolicy: z
        .object({
          startDate: z.string().nullable(),
          endDate: z.string().nullable(),
          value: z.number().nullable(),
        })
        .nullable(),
      isRefundable: z.boolean().nullable(),
      hasBreakFast: z.boolean().nullable(),
    })
    .nullable(),
  hotelPlacement: z
    .object({
      maxDistance: z.number().nullable(),
      geoLocation: z.array(z.number()).nullable(),
      isSearchedHotel: z.boolean().nullable(),
      supplierType: z.string().nullable(),
    })
    .nullable(),
  hotelBookingMode: z
    .object({
      isManual: z.boolean().nullable(),
      isBackoffice: z.boolean().nullable(),
      directBooking: z.boolean().nullable(),
      directBookingReason: z.string().nullable(),
    })
    .nullable(),
  quotationHotel: z.array(HotelCandidateSchema).nullable(),
  hotelPolicyCaps: z
    .object({
      maxPrice: z.number().nullable(),
      maxPricePerCity: z.array(z.object({ city: z.string(), price: z.number() })).nullable(),
      cheapestPriceIncreaseMargin: z.number().nullable(),
      minRating: z.number().nullable(),
    })
    .nullable(),
});

/** Per-modal quotation/flexibility adapter; `null` for manual/backoffice vouchers with no quotation. */
const TriageAdapterSchema = z.discriminatedUnion('kind', [FlightAdapterSchema, HotelAdapterSchema]);

export const TriageBlockSchema = z.object({
  policyEvaluation: PolicyEvaluationSchema,
  // Loose policy-config snapshot (`appliedPolicyRules`): source of the cache-key
  // hash + hotel caps. Not interpreted here — carried for the engine.
  policySnapshot: z.record(z.string(), z.unknown()).nullable(),
  pricingBenchmark: PricingBenchmarkSchema,
  expiration: z.string().nullable(),
  requestedAt: z.string().nullable(),
  actions: ActionsSchema,
  approvalContext: ApprovalContextSchema,
  traveler: TriagePartySchema.nullable(),
  requester: TriagePartySchema.nullable(),
  isUserVip: z.boolean().nullable().optional(),
  approvers: z.array(ApproverSchema),
  corporateGroup: z.object({ id: z.string().nullable(), name: z.string().nullable() }).nullable(),
  company: z
    .object({ id: z.union([z.number(), z.string()]).nullable(), name: z.string().nullable() })
    .nullable(),
  origin: OriginSchema.nullable(),
  flowFlags: FlowFlagsSchema.nullable(),
  associatedVoucher: z.string().nullable(),
  // null for manual/backoffice vouchers (no quotation) — the triage engine's
  // EXCEPTION_MANUAL bucket.
  adapter: TriageAdapterSchema.nullable(),
});
export type TriageBlock = z.infer<typeof TriageBlockSchema>;

export const PendingApprovalTriageViewSchema = z.object({
  voucher: VoucherViewSchema,
  triage: TriageBlockSchema.nullable(),
  approverLocale: LocaleSchema,
});
export type PendingApprovalTriageView = z.infer<typeof PendingApprovalTriageViewSchema>;
